import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, ChevronDown, ShieldCheck, TrendingDown, Target, Shield, ArrowRight, X } from 'lucide-react';
import { CTAButton, FadeIn } from '../components/UI';
export function LandingPage() {
  return <div className="min-h-screen font-sans text-gray-800" data-id="element-4">
      {/* 1. Hero */}
      <section className="bg-navy text-white pt-20 pb-24 px-6 relative overflow-hidden" data-id="element-5">
        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-6">
          <FadeIn data-id="element-7">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-serif leading-tight mb-6" data-id="element-8">
              Detén el sangrado financiero: El sistema de 90 días para liquidar
              tus tarjetas de crédito y recuperar el sueño.
            </h1>
          </FadeIn>
          <FadeIn delay={0.2} data-id="element-9">
            <h2 className="text-xl md:text-2xl text-gray-300 font-medium mb-10 max-w-3xl mx-auto" data-id="element-10">
              Elimina tus deudas sin préstamos nuevos, sin trucos bancarios y
              sin sacrificar toda tu vida social.
            </h2>
          </FadeIn>
          <FadeIn delay={0.4} data-id="element-11">
            <div className="relative mx-auto max-w-3xl mb-12" data-id="element-12">
              <div className="absolute inset-0 bg-orange/20 blur-3xl rounded-full" data-id="element-13"></div>
              <img src="https://cdn.magicpatterns.com/uploads/x7N6YR3xBZf8NgvPiRxnNn/cero_deuda1.png" alt="Cero Deudas en 90 Días" className="relative z-10 w-full h-auto rounded-xl shadow-2xl" data-id="element-14" />
            </div>
          </FadeIn>
          <FadeIn delay={0.6} data-id="element-15">
            <CTAButton data-id="element-16">¡QUIERO CERO DEUDAS AHORA!</CTAButton>
          </FadeIn>
        </div>
      </section>

      {/* 2. Opening Hook */}
      <section className="bg-white py-20 px-6" data-id="element-17">
        <div className="max-w-3xl mx-auto" data-id="element-18">
          <FadeIn data-id="element-19">
            <div className="border-l-4 border-orange pl-6 py-2" data-id="element-20">
              <p className="text-xl md:text-2xl italic text-gray-700 leading-relaxed mb-6" data-id="element-21">
                "Son las 11:30 PM. Estás frente a la pantalla del celular viendo
                el portal de tu banco. Acabas de hacer un pago de $300, pero
                cuando miras el saldo de nuevo, te das cuenta de que solo bajó
                $20. El resto se lo tragó el interés."
              </p>
              <p className="text-lg text-gray-600" data-id="element-22">
                Ese nudo en el estómago no es tu culpa. Es el resultado de un
                sistema diseñado para mantenerte pagando el mínimo durante los
                próximos 15 años.{' '}
                <strong className="text-navy" data-id="element-23">
                  Pero hoy, ese ciclo se rompe.
                </strong>
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 3. The Problem */}
      <section className="bg-light py-20 px-6" data-id="element-24">
        <div className="max-w-3xl mx-auto" data-id="element-25">
          <FadeIn data-id="element-26">
            <h2 className="text-3xl md:text-4xl font-bold font-serif text-navy mb-8 text-center" data-id="element-27">
              La trampa del pago mínimo
            </h2>
            <p className="text-lg mb-6 leading-relaxed" data-id="element-28">
              La mayoría de las personas atrapadas en deudas cometen el mismo
              error: intentan pagar un poco a cada tarjeta. El resultado es que
              no ven progreso en ninguna. Sientes que estás trabajando solo para
              enriquecer al banco mientras tu paz mental desaparece.
            </p>
            <p className="text-lg font-medium text-navy bg-white p-6 rounded-lg shadow-sm border border-gray-100" data-id="element-29">
              El estrés financiero no se va con "ahorrar en café". Se va con un
              ataque estratégico que los bancos odian porque les quita el
              control sobre tu sueldo.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* 4. Why What You Tried Didn't Work */}
      <section className="bg-white py-20 px-6" data-id="element-30">
        <div className="max-w-4xl mx-auto" data-id="element-31">
          <FadeIn data-id="element-32">
            <h2 className="text-3xl md:text-4xl font-bold font-serif text-navy mb-12 text-center" data-id="element-33">
              Por qué lo que intentaste antes falló
            </h2>
            <div className="space-y-6" data-id="element-34">
              {['Intentaste pagar la tarjeta con el interés más alto, pero el saldo era tan grande que te rendiste a los tres meses por falta de resultados visibles.', 'Pediste un préstamo de consolidación, pero al no cambiar el sistema, terminaste con el préstamo y las tarjetas llenas otra vez.', 'Seguiste el consejo del banco de "solo pagar el mínimo", asegurando que seas su cliente cautivo por el resto de tu vida productiva.', 'Intentaste presupuestos extremos que te hicieron sentir miserable, provocando gastos por ansiedad después de una semana.'].map((item, index) => <FadeIn key={index} delay={index * 0.1} data-id="element-35">
                  <div className="flex items-start bg-light p-6 rounded-xl" data-id="element-36">
                    <XCircle className="w-8 h-8 text-red-500 flex-shrink-0 mr-4 mt-1" data-id="element-37" />
                    <p className="text-lg text-gray-700" data-id="element-38">{item}</p>
                  </div>
                </FadeIn>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 5. The Unique Mechanism */}
      <section className="bg-navy text-white py-24 px-6 relative" data-id="element-39">
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-orange to-yellow-500" data-id="element-40"></div>
        <div className="max-w-4xl mx-auto text-center" data-id="element-41">
          <FadeIn data-id="element-42">
            <h2 className="text-sm font-bold tracking-widest text-orange uppercase mb-4" data-id="element-43">
              El Secreto
            </h2>
            <h3 className="text-4xl md:text-5xl font-bold font-serif mb-8" data-id="element-44">
              El Método de la Cascada de Alivio
            </h3>
            <p className="text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto mb-12" data-id="element-45">
              A diferencia de las matemáticas tradicionales, este sistema se
              basa en la psicología humana. Atacamos las deudas por tamaño de
              saldo, no por tasa de interés.
            </p>

            <div className="bg-white/10 border border-white/20 p-8 rounded-2xl backdrop-blur-sm text-left" data-id="element-46">
              <p className="text-lg leading-relaxed" data-id="element-47">
                Cada vez que cierras una cuenta por pequeña que sea, liberas
                flujo de caja y obtienes una victoria psicológica que te da el
                impulso para destruir la siguiente.{' '}
                <strong className="text-orange font-bold" data-id="element-48">
                  Es una reacción en cadena de libertad financiera.
                </strong>
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 6. What You Get */}
      <section className="bg-white py-24 px-6" data-id="element-49">
        <div className="max-w-4xl mx-auto" data-id="element-50">
          <FadeIn data-id="element-51">
            <h2 className="text-3xl md:text-4xl font-bold font-serif text-navy mb-12 text-center" data-id="element-52">
              Lo que obtienes hoy
            </h2>
            <div className="grid gap-6 mb-16" data-id="element-53">
              {[{
              title: 'El eBook "Cero Deudas en 90 Días"',
              desc: 'con la estrategia paso a paso.'
            }, {
              title: 'El Framework: Mapa de Calor de Intereses',
              desc: 'para identificar tus fugas de dinero.'
            }, {
              title: 'Guion de Negociación',
              desc: 'Qué decirle exactamente al banco para que bajen tu tasa de interés hoy mismo.'
            }, {
              title: 'Plan de Blindaje de Emergencia',
              desc: 'Cómo crear tu primer colchón de $500 para no volver a tocar las tarjetas.'
            }, {
              title: 'Hojas de Trabajo',
              desc: 'Tu calendario de pagos personalizado para los próximos 3 meses.'
            }].map((item, index) => <FadeIn key={index} delay={index * 0.1} data-id="element-54">
                  <div className="flex items-center p-6 bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow" data-id="element-55">
                    <CheckCircle2 className="w-8 h-8 text-green-500 flex-shrink-0 mr-6" data-id="element-56" />
                    <div data-id="element-57">
                      <h4 className="text-xl font-bold text-navy" data-id="element-58">
                        {item.title}
                      </h4>
                      <p className="text-gray-600 mt-1" data-id="element-59">{item.desc}</p>
                    </div>
                  </div>
                </FadeIn>)}
            </div>
            <div className="text-center" data-id="element-60">
              <CTAButton data-id="element-61">¡QUIERO CERO DEUDAS AHORA!</CTAButton>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 7. Who This Is For / Not For */}
      <section className="bg-light py-24 px-6" data-id="element-62">
        <div className="max-w-5xl mx-auto" data-id="element-63">
          <div className="grid md:grid-cols-2 gap-12" data-id="element-64">
            <FadeIn data-id="element-65">
              <div className="bg-white p-8 rounded-2xl shadow-sm h-full border-t-4 border-green-500" data-id="element-66">
                <h3 className="text-2xl font-bold font-serif text-navy mb-8 flex items-center" data-id="element-67">
                  <CheckCircle2 className="w-6 h-6 text-green-500 mr-3" data-id="element-68" />
                  Esto ES para ti si:
                </h3>
                <ul className="space-y-6" data-id="element-69">
                  {['Personas con 2 o más tarjetas de crédito que sienten que "no llegan a fin de mes".', 'Trabajadores que ven cómo los intereses devoran el 80% de sus esfuerzos de pago.', 'Padres y madres que quieren recuperar la paz mental para su familia.', 'Cualquiera que esté cansado de vivir en la "neblina financiera" y quiera numeros claros.'].map((item, i) => <li key={i} className="flex items-start" data-id="element-70">
                      <div className="w-2 h-2 rounded-full bg-green-500 mt-2 mr-3 flex-shrink-0" data-id="element-71"></div>
                      <span className="text-gray-700" data-id="element-72">{item}</span>
                    </li>)}
                </ul>
              </div>
            </FadeIn>

            <FadeIn delay={0.2} data-id="element-73">
              <div className="bg-white p-8 rounded-2xl shadow-sm h-full border-t-4 border-red-500" data-id="element-74">
                <h3 className="text-2xl font-bold font-serif text-navy mb-8 flex items-center" data-id="element-75">
                  <XCircle className="w-6 h-6 text-red-500 mr-3" data-id="element-76" />
                  Esto NO es para ti si:
                </h3>
                <ul className="space-y-6" data-id="element-77">
                  {['Personas que buscan una solución mágica para que su deuda desaparezca sin pagar nada.', 'Quienes ya están en proceso de quiebra legal o litigio.', 'Personas que no están dispuestas a dedicar 15 minutos a organizar sus estados de cuenta.'].map((item, i) => <li key={i} className="flex items-start" data-id="element-78">
                      <div className="w-2 h-2 rounded-full bg-red-500 mt-2 mr-3 flex-shrink-0" data-id="element-79"></div>
                      <span className="text-gray-700" data-id="element-80">{item}</span>
                    </li>)}
                </ul>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* 8. How It Works */}
      <section className="bg-white py-24 px-6" data-id="element-81">
        <div className="max-w-5xl mx-auto" data-id="element-82">
          <FadeIn data-id="element-83">
            <h2 className="text-3xl md:text-4xl font-bold font-serif text-navy mb-16 text-center" data-id="element-84">
              Cómo funciona el sistema
            </h2>
            <div className="grid md:grid-cols-3 gap-8 relative" data-id="element-85">
              {/* Connecting line for desktop */}
              <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-0.5 bg-gray-200 z-0" data-id="element-86"></div>

              {[{
              step: 1,
              title: 'Identificas',
              desc: 'Usas la matriz de diagnóstico para ver el daño real de tus deudas.',
              icon: <Target className="w-8 h-8 text-orange" data-id="element-87" />
            }, {
              step: 2,
              title: 'Atacas',
              desc: 'Aplicas la Cascada de Alivio al saldo más pequeño para liberar tu primer flujo de caja.',
              icon: <TrendingDown className="w-8 h-8 text-orange" data-id="element-88" />
            }, {
              step: 3,
              title: 'Blindas',
              desc: 'Sigues el protocolo de emergencia para que ningún imprevisto te obligue a usar plástico otra vez.',
              icon: <Shield className="w-8 h-8 text-orange" data-id="element-89" />
            }].map((item, index) => <FadeIn key={index} delay={index * 0.2} className="relative z-10" data-id="element-90">
                  <div className="bg-white border border-gray-100 p-8 rounded-2xl shadow-lg text-center h-full flex flex-col items-center" data-id="element-91">
                    <div className="w-20 h-20 bg-orange/10 rounded-full flex items-center justify-center mb-6 relative" data-id="element-92">
                      {item.icon}
                      <div className="absolute -top-2 -right-2 w-8 h-8 bg-navy text-white rounded-full flex items-center justify-center font-bold" data-id="element-93">
                        {item.step}
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold text-navy mb-4" data-id="element-94">
                      {item.title}
                    </h3>
                    <p className="text-gray-600" data-id="element-95">{item.desc}</p>
                  </div>
                </FadeIn>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 9. Proof / Believability */}
      <section className="bg-orange text-white py-20 px-6" data-id="element-96">
        <div className="max-w-4xl mx-auto text-center" data-id="element-97">
          <FadeIn data-id="element-98">
            <ShieldCheck className="w-16 h-16 mx-auto mb-8 opacity-90" data-id="element-99" />
            <h2 className="text-3xl md:text-4xl font-bold font-serif mb-8 leading-tight" data-id="element-100">
              En los primeros 30 días, verás tu primera cuenta en $0.
            </h2>
            <p className="text-xl md:text-2xl font-medium opacity-90 max-w-3xl mx-auto" data-id="element-101">
              No es una promesa abstracta; es el resultado matemático de
              concentrar todo tu poder de pago en un solo punto en lugar de
              dispersarlo en cinco.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* 10. FAQ */}
      <section className="bg-light py-24 px-6" data-id="element-102">
        <div className="max-w-3xl mx-auto" data-id="element-103">
          <FadeIn data-id="element-104">
            <h2 className="text-3xl md:text-4xl font-bold font-serif text-navy mb-12 text-center" data-id="element-105">
              Preguntas Frecuentes
            </h2>
            <div className="space-y-4" data-id="element-106">
              <FAQItem question="¿Esto afectará mi crédito?" answer="A largo plazo lo mejorará drásticamente al bajar tu nivel de endeudamiento." data-id="element-107" />
              <FAQItem question="¿Necesito ganar mucho dinero?" answer="No, el método funciona optimizando lo que ya ganas hoy." data-id="element-108" />
              <FAQItem question="¿Tengo que dejar de usar mis tarjetas?" answer="Sí, durante el proceso de 90 días las 'congelamos' para que el método funcione." data-id="element-109" />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 11. Offer + Price Block */}
      <section className="bg-navy text-white py-24 px-6" data-id="element-110">
        <div className="max-w-3xl mx-auto text-center" data-id="element-111">
          <FadeIn data-id="element-112">
            <h2 className="text-3xl md:text-4xl font-bold font-serif mb-8" data-id="element-113">
              Recupera el control de tu vida financiera
            </h2>
            <p className="text-xl text-gray-300 mb-12" data-id="element-114">
              por menos de lo que cuesta una salida al cine. No es un gasto, es
              la inversión más barata que harás para dejar de regalarle tu
              dinero a los bancos.
            </p>

            <div id="offer" className="bg-white text-navy p-10 rounded-3xl shadow-2xl max-w-xl mx-auto mb-10 transform hover:scale-105 transition-transform duration-300 scroll-mt-8" data-id="element-115">
              <p className="text-gray-500 font-medium uppercase tracking-wider mb-2" data-id="element-116">
                Precio Único
              </p>
              <div className="flex items-center justify-center gap-4 mb-6" data-id="element-117">
                <span className="text-3xl text-gray-400 line-through font-bold" data-id="element-118">
                  $47.00
                </span>
                <span className="text-6xl font-bold text-orange" data-id="element-119">
                  $6.39 <span className="text-2xl text-gray-500" data-id="element-120">USD</span>
                </span>
              </div>

              <CTAButton className="w-full py-5 text-xl mb-6 shadow-orange/30" data-id="element-121">
                ¡QUIERO CERO DEUDAS AHORA!
              </CTAButton>

              <div className="flex items-center justify-center text-sm text-gray-500 gap-2" data-id="element-122">
                <ShieldCheck className="w-4 h-4 text-green-500" data-id="element-123" />
                <span data-id="element-124">
                  Acceso inmediato tras la compra. Tu privacidad está
                  garantizada.
                </span>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 12. Footer */}
      <Footer data-id="element-125" />
    </div>;
}
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-id="element-126">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full px-6 py-5 text-left flex justify-between items-center focus:outline-none" data-id="element-127">
        <span className="font-bold text-lg text-navy" data-id="element-128">{question}</span>
        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-129" />
      </button>
      <AnimatePresence data-id="element-130">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-131">
            <div className="px-6 pb-5 text-gray-600 border-t border-gray-100 pt-4" data-id="element-132">
              {answer}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function Footer() {
  const [modal, setModal] = useState<'privacy' | 'terms' | null>(null);
  return <>
      <footer className="bg-[#0f172a] text-gray-400 py-12 px-6 text-center" data-id="element-133">
        <div className="max-w-4xl mx-auto" data-id="element-134">
          <p className="mb-4" data-id="element-135">
            © {new Date().getFullYear()} Cero Deudas. Todos los derechos
            reservados.
          </p>
          <div className="flex items-center justify-center gap-6 mb-6" data-id="element-136">
            <button onClick={() => setModal('privacy')} className="text-sm text-gray-400 hover:text-white transition-colors underline underline-offset-2" data-id="element-137">
              Política de Privacidad
            </button>
            <span className="text-gray-600" data-id="element-138">|</span>
            <button onClick={() => setModal('terms')} className="text-sm text-gray-400 hover:text-white transition-colors underline underline-offset-2" data-id="element-139">
              Términos y Condiciones
            </button>
          </div>
          <p className="text-sm opacity-70 max-w-2xl mx-auto" data-id="element-140">
            Este sitio no es parte del sitio web de Facebook o Facebook Inc.
            Además, este sitio NO está respaldado por Facebook de ninguna
            manera. FACEBOOK es una marca registrada de FACEBOOK, Inc. Los
            resultados pueden variar y dependen del esfuerzo individual.
          </p>
        </div>
      </footer>

      <AnimatePresence data-id="element-141">
        {modal && <LegalModal type={modal} onClose={() => setModal(null)} data-id="element-142" />}
      </AnimatePresence>
    </>;
}
function LegalModal({
  type,
  onClose
}: {
  type: 'privacy' | 'terms';
  onClose: () => void;
}) {
  const isPrivacy = type === 'privacy';
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose} data-id="element-143">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.3,
      ease: 'easeOut'
    }} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-144">
        <div className="flex items-center justify-between p-6 border-b border-gray-100" data-id="element-145">
          <h3 className="text-2xl font-bold font-serif text-navy" data-id="element-146">
            {isPrivacy ? 'Política de Privacidad' : 'Términos y Condiciones'}
          </h3>
          <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors" data-id="element-147">
            <X className="w-5 h-5 text-gray-500" data-id="element-148" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)] text-gray-700 space-y-4 text-sm leading-relaxed" data-id="element-149">
          {isPrivacy ? <>
              <p className="text-gray-500 text-xs" data-id="element-150">
                Última actualización:{' '}
                {new Date().toLocaleDateString('es-ES', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
              </p>
              <h4 className="font-bold text-navy text-base" data-id="element-151">
                1. Información que Recopilamos
              </h4>
              <p data-id="element-152">
                Recopilamos información personal que usted nos proporciona
                voluntariamente al realizar una compra, incluyendo: nombre,
                dirección de correo electrónico y datos de pago. No almacenamos
                información de tarjetas de crédito directamente; los pagos son
                procesados por plataformas de pago seguras de terceros.
              </p>
              <h4 className="font-bold text-navy text-base" data-id="element-153">
                2. Uso de la Información
              </h4>
              <p data-id="element-154">
                Utilizamos su información personal exclusivamente para: procesar
                su compra y entregar el producto digital adquirido, enviar
                confirmaciones de compra y actualizaciones relacionadas con su
                pedido, y mejorar nuestros productos y servicios.
              </p>
              <h4 className="font-bold text-navy text-base" data-id="element-155">
                3. Protección de Datos
              </h4>
              <p data-id="element-156">
                Implementamos medidas de seguridad técnicas y organizativas para
                proteger su información personal contra acceso no autorizado,
                alteración, divulgación o destrucción. Utilizamos cifrado SSL
                para todas las transacciones.
              </p>
              <h4 className="font-bold text-navy text-base" data-id="element-157">
                4. Compartir Información
              </h4>
              <p data-id="element-158">
                No vendemos, intercambiamos ni transferimos su información
                personal a terceros. Esto no incluye terceros de confianza que
                nos ayudan a operar nuestro sitio web o a realizar nuestro
                negocio, siempre que dichas partes acuerden mantener esta
                información confidencial.
              </p>
              <h4 className="font-bold text-navy text-base" data-id="element-159">5. Cookies</h4>
              <p data-id="element-160">
                Nuestro sitio puede utilizar cookies para mejorar la experiencia
                del usuario. Puede configurar su navegador para rechazar
                cookies, aunque esto podría afectar algunas funcionalidades del
                sitio.
              </p>
              <h4 className="font-bold text-navy text-base" data-id="element-161">6. Sus Derechos</h4>
              <p data-id="element-162">
                Usted tiene derecho a acceder, rectificar o eliminar sus datos
                personales en cualquier momento. Para ejercer estos derechos,
                puede contactarnos a través de los canales proporcionados en
                este sitio.
              </p>
            </> : <>
              <p className="text-gray-500 text-xs" data-id="element-163">
                Última actualización:{' '}
                {new Date().toLocaleDateString('es-ES', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
              </p>
              <h4 className="font-bold text-navy text-base" data-id="element-164">
                1. Aceptación de los Términos
              </h4>
              <p data-id="element-165">
                Al acceder y utilizar este sitio web y adquirir nuestros
                productos digitales, usted acepta estar sujeto a estos Términos
                y Condiciones. Si no está de acuerdo con alguna parte de estos
                términos, no debe utilizar nuestro sitio.
              </p>
              <h4 className="font-bold text-navy text-base" data-id="element-166">
                2. Producto Digital
              </h4>
              <p data-id="element-167">
                El producto ofrecido es un eBook digital con contenido educativo
                sobre estrategias de manejo de deudas. El contenido es de
                carácter informativo y educativo, y no constituye asesoría
                financiera profesional personalizada.
              </p>
              <h4 className="font-bold text-navy text-base" data-id="element-168">
                3. Entrega del Producto
              </h4>
              <p data-id="element-169">
                Tras completar la compra, recibirá acceso inmediato al producto
                digital. Debido a la naturaleza digital del producto, todas las
                ventas son finales una vez que se ha entregado el acceso al
                contenido.
              </p>
              <h4 className="font-bold text-navy text-base" data-id="element-170">
                4. Propiedad Intelectual
              </h4>
              <p data-id="element-171">
                Todo el contenido, incluyendo textos, gráficos, plantillas y
                materiales incluidos en el producto, está protegido por derechos
                de autor. Queda prohibida la reproducción, distribución o
                reventa del material sin autorización expresa por escrito.
              </p>
              <h4 className="font-bold text-navy text-base" data-id="element-172">
                5. Descargo de Responsabilidad
              </h4>
              <p data-id="element-173">
                Los resultados financieros pueden variar según las
                circunstancias individuales de cada persona. No garantizamos
                resultados específicos. El éxito depende del compromiso y la
                situación financiera particular de cada usuario.
              </p>
              <h4 className="font-bold text-navy text-base" data-id="element-174">
                6. Limitación de Responsabilidad
              </h4>
              <p data-id="element-175">
                No seremos responsables de ningún daño directo, indirecto,
                incidental o consecuente que resulte del uso o la incapacidad de
                usar nuestros productos o servicios. El uso del material es bajo
                su propia responsabilidad.
              </p>
              <h4 className="font-bold text-navy text-base" data-id="element-176">
                7. Modificaciones
              </h4>
              <p data-id="element-177">
                Nos reservamos el derecho de modificar estos términos en
                cualquier momento. Las modificaciones entrarán en vigor
                inmediatamente después de su publicación en el sitio web.
              </p>
            </>}
        </div>
      </motion.div>
    </motion.div>;
}